import { useAlbumsControllerAlbumsList } from "@/lib/lutely-api/gen/albums/albums";
import { useTracksControllerTracksList } from "@/lib/lutely-api/gen/tracks/tracks";
import { useCreatorsControllerCreatorsList } from "@/lib/lutely-api/gen/creators/creators";
import type {
  ListAlbumDto,
  ListTrackDto,
  ListCreatorDto,
} from "@/lib/lutely-api/gen/dtos";

interface UseSearchResultsOptions {
  enabled?: boolean;
  staleTime?: number;
}

interface UseSearchResultsReturn {
  albums: ListAlbumDto[];
  songs: ListTrackDto[];
  creators: ListCreatorDto[];
  hasResults: boolean;
  isLoading: boolean;
  canSearch: boolean;
}

export function useSearchResults(
  searchTerm: string,
  options: UseSearchResultsOptions = {},
): UseSearchResultsReturn {
  const { enabled = true, staleTime = 10_000 } = options;

  const trimmedSearchTerm = searchTerm.trim();
  const canSearch = trimmedSearchTerm.length >= 2;
  const shouldQuery = canSearch && enabled;

  const albumsQuery = useAlbumsControllerAlbumsList(
    {
      pageNumber: 0,
      pageSize: 5,
      published: true,
      search: trimmedSearchTerm,
    },
    {
      query: {
        enabled: shouldQuery,
        staleTime,
      },
    },
  );

  const songsQuery = useTracksControllerTracksList(
    {
      pageNumber: 0,
      pageSize: 5,
      type: "SONG",
      published: true,
      search: trimmedSearchTerm,
    },
    {
      query: {
        enabled: shouldQuery,
        staleTime,
      },
    },
  );

  const creatorsQuery = useCreatorsControllerCreatorsList(
    {
      pageNumber: 0,
      pageSize: 5,
      search: trimmedSearchTerm,
    },
    {
      query: {
        enabled: shouldQuery,
        staleTime,
      },
    },
  );

  const albums = (albumsQuery.data?.content ?? []) as ListAlbumDto[];
  const songs = (songsQuery.data?.content ?? []) as ListTrackDto[];
  const creators = (creatorsQuery.data?.content ?? []) as ListCreatorDto[];

  const hasResults =
    albums.length > 0 || songs.length > 0 || creators.length > 0;
  const isLoading =
    albumsQuery.isFetching || songsQuery.isFetching || creatorsQuery.isFetching;

  return {
    albums,
    songs,
    creators,
    hasResults,
    isLoading,
    canSearch,
  };
}
